import { appLocalStorage } from '../utils/storage';
import { isAndroidApp } from '../notifications/androidPush';
import config from '../utils/config';
import { APP_ONBOARDING_KEY, excludeExistingInstallation, hasProgress, inheritGuestProgress, isPlainHome, resetProgress, resumeVisit } from './state';

const INSTALL_KEY = 'onboarding:android-install';
const APP_SCOPE = APP_ONBOARDING_KEY;

function hasPreviousUse() {
  try {
    // These exist only after actual use, unlike language preferences written
    // during module initialization. iOS has its own WebView storage.
    return [config.ftoken, config.token, config.user].some(key => appLocalStorage.getItem(key) !== null);
  } catch { return true; } // Uncertain storage must not start an upgrade tutorial.
}

function initializeProgress(android, freshInstall) {
  if (freshInstall) resetProgress(APP_SCOPE);
  if (hasProgress(APP_SCOPE)) return;
  if (!android && hasProgress()) {
    inheritGuestProgress(APP_SCOPE);
  } else if (android || hasPreviousUse()) {
    // Legacy Android wrappers have no install marker. Chrome's shared storage
    // cannot distinguish them from a fresh install; default to no new course.
    excludeExistingInstallation();
  } else {
    // Persist before the first API request writes the visitor token.
    resetProgress(APP_SCOPE);
  }
}

// UI-only marker: consume before Router/analytics; never grants authentication
// or access to native APIs. TWA's Chrome storage survives wrapper uninstall.
export function prepareOnboardingLaunch(target = window) {
  const address = new URL(target.location.href);
  const marker = address.searchParams.get('wiw_onboarding');
  const origin = address.searchParams.get('wiw_onboarding_origin');
  if (marker !== null || origin !== null) {
    address.searchParams.delete('wiw_onboarding');
    address.searchParams.delete('wiw_onboarding_origin');
    target.history.replaceState(target.history.state, '', address);
  }
  const native = target.Capacitor?.isNativePlatform?.() === true || address.protocol === 'capacitor:';
  const android = isAndroidApp();
  const installed = native || android || target.navigator.standalone === true
    || target.matchMedia?.('(display-mode: standalone)').matches === true
    || target.matchMedia?.('(display-mode: fullscreen)').matches === true;
  if (!installed) return null;
  let freshInstall = false;
  if (android && /^[a-f0-9]{8}(?:-[a-f0-9]{4}){3}-[a-f0-9]{12}$/i.test(marker || '')) {
    try {
      if (appLocalStorage.getItem(INSTALL_KEY) !== marker) {
        freshInstall = origin === 'install';
        appLocalStorage.setItem(INSTALL_KEY, marker);
      }
    } catch { /* Persistence is optional. */ }
  }
  initializeProgress(android, freshInstall);
  // Resume after an earlier external-link visit, only on a normal home launch.
  if (isPlainHome(address)) resumeVisit(APP_SCOPE);
  return APP_SCOPE;
}
